import { useEffect, useRef, useState } from "react";
import {
  createAdkConversationController,
  type AdkConversationController,
  type AdkConversationOptions,
  type AdkConversationState,
} from "@/core/runtime";

export interface UseAdkConversationResult extends AdkConversationState {
  controller: AdkConversationController;
  startChat: AdkConversationController["startChat"];
  stopChat: AdkConversationController["stopChat"];
  reChat: AdkConversationController["reChat"];
  clearChat: AdkConversationController["clearChat"];
  suggestChat: AdkConversationController["suggestChat"];
  confirmFnCall: AdkConversationController["confirmFnCall"];
  switchSession: AdkConversationController["switchSession"];
  setCurrentSession: AdkConversationController["setCurrentSession"];
  replaceSessionEvents: AdkConversationController["replaceSessionEvents"];
  syncSessionEvents: AdkConversationController["syncSessionEvents"];
  deleteSession: AdkConversationController["deleteSession"];
  updateSession: AdkConversationController["updateSession"];
  likeMessage: AdkConversationController["likeMessage"];
  setMessages: AdkConversationController["setMessages"];
  setStateDelta: AdkConversationController["setStateDelta"];
}

/**
 * React 绑定层：把无框架的 AdkConversationController 包装成 Hook。
 *
 * 注意：controller 只在首次渲染创建一次；如果业务需要切换 transport / requestBuilder，
 * 建议在外层通过 key 重新挂载组件，避免对话进行中替换数据流内核。
 */
export const useAdkConversation = (
  options: AdkConversationOptions,
): UseAdkConversationResult => {
  const controllerRef = useRef<AdkConversationController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = createAdkConversationController(options);
  }
  const controller = controllerRef.current;
  const [snapshot, setSnapshot] = useState(() => controller.getSnapshot());

  useEffect(() => {
    // 订阅 controller 状态变化，并在组件卸载时销毁流式连接和轮询。
    setSnapshot(controller.getSnapshot());
    const unsubscribe = controller.subscribe(setSnapshot);
    void controller.init();
    return () => {
      unsubscribe();
      controller.destroy();
    };
  }, [controller]);

  return {
    ...snapshot,
    controller,
    startChat: controller.startChat,
    stopChat: controller.stopChat,
    reChat: controller.reChat,
    clearChat: controller.clearChat,
    suggestChat: controller.suggestChat,
    confirmFnCall: controller.confirmFnCall,
    switchSession: controller.switchSession,
    setCurrentSession: controller.setCurrentSession,
    replaceSessionEvents: controller.replaceSessionEvents,
    syncSessionEvents: controller.syncSessionEvents,
    deleteSession: controller.deleteSession,
    updateSession: controller.updateSession,
    likeMessage: controller.likeMessage,
    setMessages: controller.setMessages,
    setStateDelta: controller.setStateDelta,
  };
};

export default useAdkConversation;
